// src/business/pages/logistics/DeliveryTracking.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import { logisticsAPI } from "../../services/businessErpService";
import { PageHeader, DataTable, Pagination, StatusBadge, FormModal, BtnSecondary } from "../../components/erp";
import Panel from "../../components/Panel";

const inputCls =
  "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mnla-accent dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100";

function formatDate(value, includeTime = false) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return includeTime ? date.toLocaleString() : date.toLocaleDateString();
}

function getDeliveryNumber(row) {
  return row?.delivery_number || `DLV-${row?.id ?? "-"}`;
}

function getPONumber(row) {
  return row?.po_number || `PO-${row?.purchase_order_id || row?.po_id || "-"}`;
}

function getLineLabel(line, index) {
  return line?.variant_title || line?.variantTitle || line?.variant_sku || line?.variantSku || `Line ${index + 1}`;
}

export default function DeliveryTracking() {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await logisticsAPI.list({ page, limit: 20, search });
      setDeliveries(res?.data || res?.items || []);
      setTotalPages(res?.pagination?.totalPages || res?.meta?.totalPages || 1);
    } catch {
      setDeliveries([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const openDetail = useCallback(async (row) => {
    try {
      const res = await logisticsAPI.getById(row.id);
      setDetail(res?.data || row);
    } catch {
      setDetail(row);
    }
  }, []);

  const columns = useMemo(() => ([
    {
      key: "delivery_number",
      label: "Delivery #",
      sortable: true,
      render: (row) => <span className="font-mono text-xs">{getDeliveryNumber(row)}</span>,
    },
    {
      key: "po_number",
      label: "PO #",
      sortable: true,
      render: (row) => <span className="font-mono text-xs">{getPONumber(row)}</span>,
    },
    { key: "supplier_name", label: "Supplier", render: (row) => row.supplier_name || "Supplier" },
    { key: "carrier", label: "Carrier", render: (row) => row.carrier || "-" },
    {
      key: "tracking_number",
      label: "Tracking #",
      render: (row) => row.tracking_number ? <span className="font-mono text-xs">{row.tracking_number}</span> : "-",
    },
    { key: "status", label: "Delivery Status", render: (row) => <StatusBadge status={row.status || "DRAFT"} /> },
    { key: "receiving_status", label: "Receiving", render: (row) => <StatusBadge status={row.receiving_status || "PENDING_RECEIPT"} /> },
    {
      key: "estimated_arrival",
      label: "ETA",
      render: (row) => formatDate(row.estimated_arrival || row.estimated_arrival_at),
    },
    {
      key: "actions",
      label: "",
      render: (row) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            openDetail(row);
          }}
          className="text-xs font-medium text-mnla-accent hover:underline"
        >
          View
        </button>
      ),
    },
  ]), [openDetail]);

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Procurement" title="Delivery Tracking" subtitle="Track supplier deliveries and receiving status" />

      <Panel
        title="Delivery Records"
        right={(
          <input
            type="text"
            placeholder="Search deliveries..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className={inputCls + " w-full sm:w-80"}
          />
        )}
      >

        <DataTable
          columns={columns}
          data={deliveries}
          loading={loading}
          onRowClick={openDetail}
          emptyMessage="No deliveries found."
        />

        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      </Panel>

      <FormModal
        open={!!detail}
        onClose={() => setDetail(null)}
        title={detail ? `${getDeliveryNumber(detail)} Details` : ""}
        size="lg"
        footer={<BtnSecondary onClick={() => setDetail(null)}>Close</BtnSecondary>}
      >
        {detail && (
          <div className="space-y-6">
            <div className="grid gap-4 text-sm md:grid-cols-2">
              <div>
                <span className="text-neutral-500 dark:text-neutral-400">Delivery #</span>
                <p className="mt-1 font-medium dark:text-neutral-100">{getDeliveryNumber(detail)}</p>
              </div>
              <div>
                <span className="text-neutral-500 dark:text-neutral-400">PO #</span>
                <p className="mt-1 font-medium dark:text-neutral-100">{getPONumber(detail)}</p>
              </div>
              <div>
                <span className="text-neutral-500 dark:text-neutral-400">Supplier</span>
                <p className="mt-1 font-medium dark:text-neutral-100">{detail.supplier_name || "Supplier"}</p>
              </div>
              <div>
                <span className="text-neutral-500 dark:text-neutral-400">Product</span>
                <p className="mt-1 font-medium dark:text-neutral-100">{detail.product_name || "-"}</p>
              </div>
              <div>
                <span className="text-neutral-500 dark:text-neutral-400">Delivery Status</span>
                <p className="mt-1"><StatusBadge status={detail.status || "DRAFT"} /></p>
              </div>
              <div>
                <span className="text-neutral-500 dark:text-neutral-400">Receiving Status</span>
                <p className="mt-1"><StatusBadge status={detail.receiving_status || "PENDING_RECEIPT"} /></p>
              </div>
              <div>
                <span className="text-neutral-500 dark:text-neutral-400">Carrier</span>
                <p className="mt-1 font-medium dark:text-neutral-100">{detail.carrier || "-"}</p>
              </div>
              <div>
                <span className="text-neutral-500 dark:text-neutral-400">Tracking #</span>
                <p className="mt-1 font-mono font-medium dark:text-neutral-100">{detail.tracking_number || "-"}</p>
              </div>
              <div>
                <span className="text-neutral-500 dark:text-neutral-400">ETA</span>
                <p className="mt-1 font-medium dark:text-neutral-100">{formatDate(detail.estimated_arrival || detail.estimated_arrival_at)}</p>
              </div>
              <div>
                <span className="text-neutral-500 dark:text-neutral-400">Created</span>
                <p className="mt-1 font-medium dark:text-neutral-100">{formatDate(detail.created_at, true)}</p>
              </div>
              <div>
                <span className="text-neutral-500 dark:text-neutral-400">Prepared At</span>
                <p className="mt-1 font-medium dark:text-neutral-100">{formatDate(detail.ready_for_dispatch_at, true)}</p>
              </div>
              <div>
                <span className="text-neutral-500 dark:text-neutral-400">Dispatched At</span>
                <p className="mt-1 font-medium dark:text-neutral-100">{formatDate(detail.dispatched_at || detail.shipped_at, true)}</p>
              </div>
              <div>
                <span className="text-neutral-500 dark:text-neutral-400">Delivered At</span>
                <p className="mt-1 font-medium dark:text-neutral-100">{formatDate(detail.delivered_at, true)}</p>
              </div>
            </div>

            {detail.notes ? (
              <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-800 dark:bg-neutral-900/40">
                <div className="text-xs font-semibold uppercase tracking-[0.24em] text-neutral-500 dark:text-neutral-400">Delivery Notes</div>
                <p className="mt-2 whitespace-pre-wrap text-sm text-neutral-700 dark:text-neutral-200">{detail.notes}</p>
              </div>
            ) : null}

            {Array.isArray(detail.lines) && detail.lines.length > 0 ? (
              <div className="space-y-3">
                <div className="text-xs font-semibold uppercase tracking-[0.24em] text-neutral-500 dark:text-neutral-400">
                  Delivery Lines ({detail.lines.length})
                </div>
                <div className="overflow-x-auto rounded-xl border border-neutral-200 dark:border-neutral-800">
                  <table className="min-w-full text-sm">
                    <thead className="bg-neutral-50 text-neutral-500 dark:bg-neutral-900/40 dark:text-neutral-400">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Line</th>
                        <th className="px-3 py-2 text-left font-medium">Variant</th>
                        <th className="px-3 py-2 text-right font-medium">Expected</th>
                        <th className="px-3 py-2 text-right font-medium">Received</th>
                        <th className="px-3 py-2 text-right font-medium">Accepted</th>
                        <th className="px-3 py-2 text-right font-medium">Rejected</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.lines.map((line, index) => (
                        <tr key={line.id || line.deliveryLineId || `${detail.id}-${index}`} className="border-t border-neutral-200 dark:border-neutral-800">
                          <td className="px-3 py-2 font-mono text-xs text-neutral-500 dark:text-neutral-400">#{index + 1}</td>
                          <td className="px-3 py-2">
                            <div className="font-medium text-neutral-800 dark:text-neutral-100">{getLineLabel(line, index)}</div>
                            {(line.variant_sku || line.variantSku) ? (
                              <div className="text-xs text-neutral-500 dark:text-neutral-400">{line.variant_sku || line.variantSku}</div>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 text-right text-neutral-700 dark:text-neutral-200">{line.expected_quantity ?? line.expectedQuantity ?? 0}</td>
                          <td className="px-3 py-2 text-right text-neutral-700 dark:text-neutral-200">{line.received_quantity ?? line.receivedQuantity ?? 0}</td>
                          <td className="px-3 py-2 text-right text-neutral-700 dark:text-neutral-200">{line.accepted_quantity ?? line.acceptedQuantity ?? 0}</td>
                          <td className="px-3 py-2 text-right text-neutral-700 dark:text-neutral-200">{line.rejected_quantity ?? line.rejectedQuantity ?? 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </FormModal>
    </div>
  );
}
